// Unified Configuration Service
// This service acts as a router that directs calls to RDS-based services
// while maintaining backward compatibility with existing code

// Import RDS-based services
import * as RDSCompatibilityService from './compatibilityMatrixRDS';
import * as RDSPricingService from './pricingConfigServiceRDS';

// Import static services as fallbacks (only for compatibility, not pricing)
import * as StaticCompatibilityService from './compatibilityMatrix';

// Import pricing service for update functions
import * as PricingService from './pricingService';

// Configuration flag to determine which service to use
let useRDS = true;

// Enable/disable RDS usage
export function setRDSEnabled(enabled) {
  useRDS = enabled;
  console.log(`🔄 Configuration service mode: ${enabled ? 'RDS' : 'Static'}`);
}

export function isRDSEnabled() {
  return useRDS;
}

// Compatibility Matrix Functions
export async function areConnectorsCompatible(connectorA, connectorB) {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.areConnectorsCompatible(connectorA, connectorB);
    } catch (error) {
      console.warn('⚠️ RDS compatibility check failed, falling back to static:', error.message);
      return StaticCompatibilityService.areConnectorsCompatible(connectorA, connectorB);
    }
  }
  return StaticCompatibilityService.areConnectorsCompatible(connectorA, connectorB);
}

export async function getConnectorCompatibility(connectorId) {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.getConnectorCompatibility(connectorId);
    } catch (error) {
      console.warn('⚠️ RDS connector compatibility failed, falling back to static:', error.message);
      return StaticCompatibilityService.getConnectorCompatibility(connectorId);
    }
  }
  return StaticCompatibilityService.getConnectorCompatibility(connectorId);
}

export async function getAllCompatibleConnectors(connectorId) {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.getAllCompatibleConnectors(connectorId);
    } catch (error) {
      console.warn('⚠️ RDS all compatible connectors failed, falling back to static:', error.message);
      return StaticCompatibilityService.getAllCompatibleConnectors(connectorId);
    }
  }
  return StaticCompatibilityService.getAllCompatibleConnectors(connectorId);
}

export async function getConnectorWireGaugeCompatibility(connectorId) {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.getConnectorWireGaugeCompatibility(connectorId);
    } catch (error) {
      console.warn('⚠️ RDS wire gauge compatibility failed, falling back to static:', error.message);
      return StaticCompatibilityService.getConnectorWireGaugeCompatibility(connectorId);
    }
  }
  return StaticCompatibilityService.getConnectorWireGaugeCompatibility(connectorId);
}

export async function getConnectorWireCompatibility(connectorId) {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.getConnectorWireCompatibility(connectorId);
    } catch (error) {
      console.warn('⚠️ RDS wire compatibility failed, falling back to static:', error.message);
      return StaticCompatibilityService.getConnectorWireCompatibility(connectorId);
    }
  }
  return StaticCompatibilityService.getConnectorWireCompatibility(connectorId);
}

export async function getFullCompatibilityMatrix() {
  if (useRDS) {
    try {
      return await RDSCompatibilityService.getFullCompatibilityMatrix();
    } catch (error) {
      console.warn('⚠️ RDS full matrix failed, falling back to static:', error.message);
      return StaticCompatibilityService.compatibilityMatrix;
    }
  }
  return StaticCompatibilityService.compatibilityMatrix;
}

// Admin functions for compatibility
export async function updateCompatibilityEntry(connectorA, connectorB, data, reason) {
  if (useRDS) {
    return await RDSCompatibilityService.updateCompatibilityEntry(connectorA, connectorB, data, reason);
  }
  console.warn('⚠️ Cannot update compatibility in static mode');
  return false;
}

export async function refreshCompatibilityMatrix() {
  if (useRDS) {
    return await RDSCompatibilityService.refreshCompatibilityMatrix();
  }
  console.warn('⚠️ Cannot refresh compatibility in static mode');
  return StaticCompatibilityService.compatibilityMatrix;
}

// Pricing Configuration Functions
export async function loadPricingConfig(isAdmin = false) {
  if (useRDS) {
    return await RDSPricingService.loadPricingConfig(isAdmin);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function savePricingConfig(config) {
  if (useRDS) {
    return await RDSPricingService.savePricingConfig(config);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateConnectorPricing(connectorId, pricing, isPanelMount) {
  if (useRDS) {
    return await RDSPricingService.updateConnectorPricing(connectorId, pricing, isPanelMount);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateCableBasePrices(cableType, price, wireGauge) {
  if (useRDS) {
    return await RDSPricingService.updateCableBasePrices(cableType, price, wireGauge);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function bulkUpdatePricing(updates) {
  if (useRDS) {
    return await RDSPricingService.bulkUpdatePricing(updates);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function refreshPricingConfig() {
  if (useRDS) {
    return await RDSPricingService.refreshPricingConfig();
  }
  throw new Error('Pricing configuration requires RDS database access');
}

// Re-export other pricing functions with RDS support
export async function updatePricingSection(section, data) {
  if (useRDS) {
    return await RDSPricingService.updatePricingSection(section, data);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updatePowerWireGaugePricing(gauge, price) {
  if (useRDS) {
    return await RDSPricingService.updatePowerWireGaugePricing(gauge, price);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateWyeCablePricing(cableType, price) {
  if (useRDS) {
    return await RDSPricingService.updateWyeCablePricing(cableType, price);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateFinishingPricing(option, price) {
  if (useRDS) {
    return await RDSPricingService.updateFinishingPricing(option, price);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateLaborCosts(quality, cost) {
  if (useRDS) {
    return await RDSPricingService.updateLaborCosts(quality, cost);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

export async function updateMarkups(category, markup) {
  if (useRDS) {
    return await RDSPricingService.updateMarkups(category, markup);
  }
  throw new Error('Pricing configuration requires RDS database access');
}

// Re-export static-only functions (only compatibility, not pricing)
export const {
  exportPricingConfig,
  importPricingConfig,
  resetPricingConfig,
  getPricingHistory,
  addPricingHistoryEntry
} = RDSPricingService;

// Re-export pricing update functions
export const {
  subscribeToPricingUpdates,
  cleanupPricingUpdates,
  getRecentPricingChanges
} = PricingService;

// Initialization functions
export async function initializeConfigSystem() {
  console.log('🔧 Initializing unified configuration system...');
  
  if (useRDS) {
    try {
      await Promise.all([
        RDSCompatibilityService.initializeCompatibilitySystem(),
        RDSPricingService.initializePricingSystem()
      ]);
      console.log('🚀 RDS-based configuration system ready!');
    } catch (error) {
      console.error('❌ RDS initialization failed:', error.message);
      throw new Error('RDS database access is required for pricing configuration');
    }
  }
  
  if (!useRDS) {
    console.log('📂 Using static configuration files (compatibility only)');
  }
}

// Health check functions
export async function checkSystemHealth() {
  const health = {
    mode: useRDS ? 'RDS' : 'static',
    timestamp: new Date().toISOString(),
    services: {}
  };
  
  if (useRDS) {
    try {
      const [compatibilityHealth, pricingHealth] = await Promise.all([
        RDSCompatibilityService.checkCompatibilitySystemHealth(),
        RDSPricingService.checkPricingSystemHealth()
      ]);
      
      health.services.compatibility = compatibilityHealth;
      health.services.pricing = pricingHealth;
      health.status = 'healthy';
    } catch (error) {
      health.status = 'degraded';
      health.error = error.message;
    }
  } else {
    health.status = 'static';
    health.services.compatibility = { status: 'static', source: 'file' };
    health.services.pricing = { status: 'static', source: 'file' };
  }
  
  return health;
}

// Migration support
export async function migrateLegacyData() {
  if (!useRDS) {
    console.log('⚠️ Migration requires RDS mode');
    return false;
  }
  
  console.log('🔄 Starting legacy data migration...');
  
  try {
    // This would trigger the migration script
    // For now, we'll just log the instruction
    console.log('📋 To migrate data, run: node scripts/migrate-to-rds.js');
    return true;
  } catch (error) {
    console.error('❌ Migration failed:', error);
    return false;
  }
}

// Live update support (for admin dashboard)
export function onConfigUpdate(callback) {
  if (useRDS) {
    // Set up live update listeners for RDS changes
    console.log('🔔 Live update listeners not yet implemented');
    // This would set up WebSocket or SSE listeners
  }
}

// Export compatibility with existing imports
export const compatibilityMatrix = new Proxy({}, {
  get: async (target, prop) => {
    const matrix = await getFullCompatibilityMatrix();
    return matrix[prop];
  }
});